import { AxiosInstance } from 'axios';
import type {
  BulkImportListItem,
  BulkImportDetail,
  BulkImportStats,
  BulkImportRow,
  BulkImportTemplate,
  BulkImportTemplateDetail,
  CreateImportRequest,
  BulkImportPaginatedResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateColumn,
  AddColumnRequest,
  UpdateColumnRequest,
  AddClientsRequest,
  TargetFieldsResponse,
} from '@trukt/types/BulkImport';

export interface ListImportsParams {
  pg?: number;
  rc?: number;
  status?: string;
  sortColumn?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface ListRowsParams {
  pg?: number;
  rc?: number;
  status?: string;
  stage?: string;
}

export class BulkImportServices {
  instance: AxiosInstance;

  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  /**
   * List bulk imports with pagination
   * GET /bulk-import/imports
   */
  listImports(params: ListImportsParams = {}) {
    return this.instance.get<BulkImportPaginatedResponse<BulkImportListItem>>(
      '/bulk-import/imports',
      { params },
    );
  }

  /**
   * Get import details
   * GET /bulk-import/imports/:guid
   */
  getImport(guid: string) {
    return this.instance.get<BulkImportDetail>(`/bulk-import/imports/${guid}`);
  }

  /**
   * Get import statistics
   * GET /bulk-import/imports/:guid/stats
   */
  getImportStats(guid: string) {
    return this.instance.get<BulkImportStats>(`/bulk-import/imports/${guid}/stats`);
  }

  /**
   * List import rows with pagination
   * GET /bulk-import/imports/:guid/rows
   */
  listImportRows(guid: string, params: ListRowsParams = {}) {
    return this.instance.get<BulkImportPaginatedResponse<BulkImportRow>>(
      `/bulk-import/imports/${guid}/rows`,
      { params },
    );
  }

  /**
   * Create an import from S3 file
   * POST /bulk-import/imports
   */
  createImport(data: CreateImportRequest) {
    return this.instance.post<{ guid: string; status: string }>('/bulk-import/imports', data);
  }

  /**
   * Upload CSV and create import (for files < 5MB)
   * POST /bulk-import/upload
   */
  uploadAndCreateImport(formData: FormData) {
    return this.instance.post<{
      guid: string;
      status: string;
      originalFilename: string;
      storageKey: string;
    }>('/bulk-import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Cancel an active import
   * DELETE /bulk-import/imports/:guid
   */
  cancelImport(guid: string) {
    return this.instance.delete(`/bulk-import/imports/${guid}`);
  }

  /**
   * List available templates
   * GET /bulk-import/templates
   */
  listTemplates() {
    return this.instance.get<BulkImportTemplate[]>('/bulk-import/templates');
  }

  /**
   * Get template details
   * GET /bulk-import/templates/:id
   */
  getTemplate(id: string) {
    return this.instance.get<BulkImportTemplateDetail>(`/bulk-import/templates/${id}`);
  }

  /**
   * Get presigned upload URL for large files
   * POST /bulk-import/upload-url
   */
  getUploadUrl(data: { filename: string; contentType: string }) {
    return this.instance.post<{
      uploadUrl: string;
      storageKey: string;
    }>('/bulk-import/upload-url', data);
  }

  /**
   * Create a new template
   * POST /bulk-import/templates
   */
  createTemplate(data: CreateTemplateRequest) {
    return this.instance.post<BulkImportTemplate>('/bulk-import/templates', data);
  }

  /**
   * Update a template
   * PATCH /bulk-import/templates/:id
   */
  updateTemplate(id: string, data: UpdateTemplateRequest) {
    return this.instance.patch<BulkImportTemplate>(`/bulk-import/templates/${id}`, data);
  }

  /**
   * Delete a template (soft delete)
   * DELETE /bulk-import/templates/:id
   */
  deleteTemplate(id: string) {
    return this.instance.delete(`/bulk-import/templates/${id}`);
  }

  /**
   * Get target fields for column mapping
   * GET /bulk-import/target-fields
   */
  getTargetFields() {
    return this.instance.get<TargetFieldsResponse>('/bulk-import/target-fields');
  }

  /**
   * Add a column mapping to a template
   * POST /bulk-import/templates/:templateId/columns
   */
  addColumn(templateId: string, data: AddColumnRequest) {
    return this.instance.post<TemplateColumn>(`/bulk-import/templates/${templateId}/columns`, data);
  }

  /**
   * Update a column mapping
   * PATCH /bulk-import/templates/:templateId/columns/:columnId
   */
  updateColumn(templateId: string, columnId: string, data: UpdateColumnRequest) {
    return this.instance.patch<TemplateColumn>(
      `/bulk-import/templates/${templateId}/columns/${columnId}`,
      data,
    );
  }

  /**
   * Delete a column mapping
   * DELETE /bulk-import/templates/:templateId/columns/:columnId
   */
  deleteColumn(templateId: string, columnId: string) {
    return this.instance.delete(`/bulk-import/templates/${templateId}/columns/${columnId}`);
  }

  /**
   * Add client associations to a template
   * POST /bulk-import/templates/:templateId/clients
   */
  addClients(templateId: string, data: AddClientsRequest) {
    return this.instance.post(`/bulk-import/templates/${templateId}/clients`, data);
  }

  /**
   * Remove a client association from a template
   * DELETE /bulk-import/templates/:templateId/clients/:clientGuid
   */
  removeClient(templateId: string, clientGuid: string) {
    return this.instance.delete(`/bulk-import/templates/${templateId}/clients/${clientGuid}`);
  }
}
